import { useState, useCallback } from "react";
import type { GameActionStartArgs } from "@skillscratch/shared-types";
import type { GameActionStartRet } from "@skillscratch/shared-types";
import { callFirebaseFunction } from "@/bridge/callFirebaseFunction";
import { retry } from "@/helpers/retry";
import type { UserGamePack } from "@skillscratch/shared-types";

type useStartGameProps = {
    onSuccess: (result: GameActionStartRet, userGamePack: UserGamePack) => void;
    onError: (error: unknown) => void;
}

export function useStartGame({ onSuccess, onError }: useStartGameProps) {
    const [isStartingGame, setIsStartingGame] = useState(false);
    const [startGameError, setStartGameError] = useState<unknown | null>(null);
    
    const startGameHandler = useCallback(async (userGamePack: UserGamePack) => {
        if (isStartingGame) {
            return;
        }

        setIsStartingGame(true);
        
        try {
            const result = await retry(() => 
            callFirebaseFunction<GameActionStartArgs, GameActionStartRet>(
                "gameAction",
                {
                    type: "start",
                    gamePackId: userGamePack.id,
                }
            ));
            
            setIsStartingGame(false);
            onSuccess(result, userGamePack);
        } catch (error: unknown) {
            setIsStartingGame(false);
            // should probably show an error/handle with sentry/posthog
            setStartGameError(error);
            onError(error);
        }
    }, [isStartingGame, onSuccess, onError]);
  
    return {
        isStartingGame,
        startGameHandler,
        startGameError,
    };
  }